'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { REMEDIES, CATEGORY_LABELS } from '@/data/remedies';
import type { RemedyCategory } from '@/data/remedies';
import { recordEngagement } from '@/lib/engagementNudge';
import RemedyImage from '@/components/RemedyImage';

// ── Problem selector config ────────────────────────────────────────────────────

const PROBLEMS: { id: string; label: string }[] = [
  { id: 'aphids',           label: 'Aphids'           },
  { id: 'caterpillars',     label: 'Caterpillars'     },
  { id: 'beetles',          label: 'Beetles'          },
  { id: 'spider-mites',     label: 'Spider mites'     },
  { id: 'whitefly',         label: 'Whitefly'         },
  { id: 'thrips',           label: 'Thrips'           },
  { id: 'fungal-disease',   label: 'Fungal disease'   },
  { id: 'powdery-mildew',   label: 'Powdery mildew'   },
  { id: 'chewed-leaves',    label: 'Chewed leaves'    },
  { id: 'sticky-residue',   label: 'Sticky residue'   },
  { id: 'soil-too-alkaline',label: 'Soil too alkaline'},
  { id: 'poor-growth',      label: 'Poor growth'      },
  { id: 'slugs-or-snails',  label: 'Slugs or snails'  },
  { id: 'crawling-insects', label: 'Crawling insects' },
  { id: 'not-sure',         label: 'Not sure'         },
];

// ── Filter config ──────────────────────────────────────────────────────────────

const FILTERS: { value: RemedyCategory | 'all'; label: string }[] = [
  { value: 'all',          label: 'All'                  },
  { value: 'pest-control', label: 'Pest Control'         },
  { value: 'fungal',       label: 'Fungal Issues'        },
  { value: 'soil-health',  label: 'Soil Health'          },
  { value: 'nutrients',    label: 'Nutrient Deficiencies'},
  { value: 'general',      label: 'General Care'         },
];

const CATEGORY_BADGE: Record<RemedyCategory, string> = {
  'pest-control': 'bg-[#C2703E] text-white',
  'fungal':       'bg-[#7C5CBF] text-white',
  'soil-health':  'bg-[#2D5016] text-white',
  'nutrients':    'bg-[#8A8678] text-white',
  'general':      'bg-[#6B8F6E] text-white',
};

// ── Icons ──────────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round">
      <path d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5" />
    </svg>
  );
}

function LeafShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 2L3 5.5V10c0 4 3 6.5 7 8 4-1.5 7-4 7-8V5.5L10 2z" />
      <path d="M10 10C9 10 6.5 9.5 6.5 7.5c0-1.5 1.5-2 2.5-1C9.5 7 10 9 10 10Z" />
      <path d="M10 10C11 10 13.5 9.5 13.5 7.5c0-1.5-1.5-2-2.5-1C10.5 7 10 9 10 10Z" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 1v3M9 14v3M1 9h3M14 9h3M3.5 3.5l2 2M12.5 12.5l2 2M14.5 3.5l-2 2M5.5 12.5l-2 2" />
    </svg>
  );
}

function WarningSmIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 1L12.5 12H0.5L6.5 1Z" />
      <path d="M6.5 5V8" />
      <circle cx="6.5" cy="9.5" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function RemediesContent() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<RemedyCategory | 'all'>('all');
  const [selectedProblem, setSelectedProblem] = useState<string | null>(null);

  useEffect(() => { recordEngagement(); }, []);

  const matchedRemedies = selectedProblem
    ? REMEDIES.filter(r => r.problemTags.includes(selectedProblem))
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const filtered = REMEDIES.filter(r => {
    if (category !== 'all' && r.category !== category) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        r.name.toLowerCase().includes(q) ||
        r.shortDescription.toLowerCase().includes(q) ||
        r.tags.some(t => t.includes(q))
      );
    }
    return true;
  }).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-5">

      {/* Back */}
      <div>
        <Link href="/learn"
          className="font-body text-[13px] text-sage hover:text-deep-green transition-colors">
          ← Learn
        </Link>
      </div>

      {/* Header */}
      <div>
        <h1 className="font-heading text-[28px] text-dark-text leading-tight">
          Garden Remedies
        </h1>
        <p className="font-body text-[16px] text-sage mt-1">
          Natural solutions for pests, diseases, and soil health
        </p>
      </div>

      {/* Pledge callout */}
      <div className="bg-light-sage rounded-xl border border-sage/30 p-4 flex gap-3 items-start">
        <span className="text-deep-green flex-shrink-0 mt-0.5"><LeafShieldIcon /></span>
        <div>
          <p className="font-body text-[14px] font-semibold text-deep-green">
            100% Organic Solutions Only
          </p>
          <p className="font-body text-[13px] text-sage mt-0.5 leading-relaxed">
            Every remedy listed here is organic, naturally derived, and safe for soil life,
            beneficial insects, and wildlife.
          </p>
        </div>
      </div>

      {/* ── Guided Matcher ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-sand p-5 space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-deep-green"><SparkleIcon /></span>
          <h2 className="font-heading text-[18px] text-dark-text">Find the Right Remedy</h2>
        </div>
        <p className="font-body text-[13px] text-sage leading-relaxed -mt-1">
          What problem are you dealing with?
        </p>

        {/* Problem chips */}
        <div className="flex flex-wrap gap-2">
          {PROBLEMS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setSelectedProblem(prev => prev === id ? null : id)}
              className={`font-body text-[13px] font-medium px-3.5 py-1.5 rounded-full border
                          transition-all duration-150 whitespace-nowrap
                          ${selectedProblem === id
                            ? 'bg-deep-green text-white border-deep-green'
                            : 'bg-white text-dark-text border-sand hover:border-sage'
                          }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Matcher results */}
        {selectedProblem && (
          <div className="space-y-3 pt-1">
            {matchedRemedies.length === 0 ? (
              <p className="font-body text-[14px] text-sage">
                No specific remedies found for this problem. Try browsing all remedies below.
              </p>
            ) : (
              <>
                <p className="font-body text-[13px] text-sage">
                  {matchedRemedies.length} {matchedRemedies.length === 1 ? 'remedy' : 'remedies'} suggested
                </p>
                <div className="space-y-3">
                  {matchedRemedies.map(remedy => (
                    <div key={remedy.id}
                      className="bg-[#F8F9F6] rounded-xl border border-sage/20 p-4 flex gap-4">
                      <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden
                                      border border-sand bg-white">
                        <RemedyImage
                          id={remedy.id}
                          name={remedy.name}
                          width={64}
                          height={64}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <h3 className="font-heading text-[16px] text-dark-text">
                            {remedy.name}
                          </h3>
                          <span className={`inline-block font-body text-[10px] font-semibold
                                            px-2 py-0.5 rounded-full flex-shrink-0
                                            ${CATEGORY_BADGE[remedy.category]}`}>
                            {CATEGORY_LABELS[remedy.category]}
                          </span>
                        </div>
                        <p className="font-body text-[13px] text-sage mt-1 leading-relaxed line-clamp-2">
                          {remedy.shortDescription}
                        </p>
                        <p className="font-body text-[12px] text-dark-text mt-1.5 leading-relaxed line-clamp-2">
                          <span className="font-semibold text-deep-green">When to use: </span>
                          {remedy.whenToUse}
                        </p>
                        {remedy.whenNotToUse && (
                          <div className="flex items-start gap-1.5 mt-1.5">
                            <span className="text-warm-brown flex-shrink-0 mt-0.5"><WarningSmIcon /></span>
                            <p className="font-body text-[12px] text-warm-brown leading-relaxed line-clamp-1">
                              {remedy.whenNotToUse}
                            </p>
                          </div>
                        )}
                        <div className="mt-2.5">
                          <Link
                            href={`/learn/remedies/${remedy.id}`}
                            className="font-body text-[13px] font-semibold text-deep-green hover:underline">
                            How to use →
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Browse all remedies ─────────────────────────────────────────────── */}
      <h2 className="font-heading text-[20px] text-dark-text pt-1">Browse all remedies</h2>

      {/* Search */}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sage pointer-events-none">
          <SearchIcon />
        </span>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search remedies..."
          className="w-full font-body text-[15px] text-dark-text bg-white border border-sand
                     rounded-xl pl-9 pr-9 py-2.5 focus:outline-none focus:border-sage
                     placeholder:text-sand transition-colors"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sand hover:text-sage transition-colors">
            <XIcon />
          </button>
        )}
      </div>

      {/* Category filter chips */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setCategory(value)}
            className={`font-body text-[13px] font-medium px-4 py-1.5 rounded-full border
                        transition-all duration-150 whitespace-nowrap
                        ${category === value
                          ? 'bg-deep-green text-white border-deep-green'
                          : 'bg-white text-dark-text border-sand hover:border-sage'
                        }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Results count */}
      <p className="font-body text-[13px] text-sage">
        {filtered.length === REMEDIES.length
          ? `${REMEDIES.length} remedies`
          : `${filtered.length} of ${REMEDIES.length} results`}
        {category !== 'all' && ` in ${CATEGORY_LABELS[category]}`}
      </p>

      {/* Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered.map(remedy => (
            <Link
              key={remedy.id}
              href={`/learn/remedies/${remedy.id}`}
              className="group bg-white rounded-xl border border-sand p-5 flex flex-col gap-3
                         hover:border-sage hover:shadow-sm transition-all duration-200">

              {/* Badge + image */}
              <div className="flex items-start justify-between gap-3">
                <span className={`inline-block font-body text-[11px] font-semibold
                                  px-2.5 py-0.5 rounded-full ${CATEGORY_BADGE[remedy.category]}`}>
                  {CATEGORY_LABELS[remedy.category]}
                </span>
                <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden
                                border border-sand bg-white">
                  <RemedyImage
                    id={remedy.id}
                    name={remedy.name}
                    width={80}
                    height={80}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              </div>

              {/* Name + description */}
              <div>
                <h2 className="font-heading text-[18px] text-dark-text leading-snug
                               group-hover:text-deep-green transition-colors">
                  {remedy.name}
                </h2>
                <p className="font-body text-[14px] text-sage mt-1 leading-relaxed line-clamp-2">
                  {remedy.shortDescription}
                </p>
              </div>

              {/* Tags */}
              {remedy.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {remedy.tags.slice(0, 3).map(tag => (
                    <span key={tag}
                      className="font-body text-[11px] text-sage bg-[#F5F5F0]
                                 px-2 py-0.5 rounded-full border border-sand">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-auto pt-1">
                <span className="font-body text-[13px] font-semibold text-deep-green
                                 group-hover:underline">
                  How to use →
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-sand p-10 flex flex-col
                        items-center justify-center text-center gap-3">
          <p className="font-heading text-[18px] text-dark-text">No results found</p>
          <p className="font-body text-[14px] text-sage">Try a different search term or filter</p>
          <button
            onClick={() => { setSearch(''); setCategory('all'); }}
            className="font-body text-[13px] font-semibold text-deep-green hover:underline">
            Clear filters
          </button>
        </div>
      )}

    </div>
  );
}
